/* ============================================================
   Desktop navigation behaviour (<desktop-nav>)
   ------------------------------------------------------------
   Pairs with snippets/header-desktop-nav.liquid and
   assets/component-desktop-nav.css.

   · Shop / Brands doors open their panel on hover (with an intent
     delay) and on click; Esc, the scrim, mouseleave and focus
     leaving the header all close. One panel at a time.
   · While a panel or the search is open, body.js-dnav-open holds the
     site's shared page scroll lock (the observer at the end of
     global.js): the dimmed page stops scrolling under the wheel, as it
     does behind the cart and filter drawers. syncOverlay() is the one
     writer of the scrim and of that class.
   · No focus trap — this is the WAI disclosure-navigation pattern,
     so Tab flows from the door into the open panel naturally.
   · The Shop rail swaps pane + feature card on hover/focus; a click
     on a rail item navigates.
   · The Brands filter debounces 220ms (mobile parity, via global.js
     debounce) and hides whole letter groups; with the panel open,
     printable keys and Backspace route to it from anywhere (modifier
     combos, IME composition and other inputs pass through).
   · The search toggle keeps its previous behaviour (moved here from
     the section's inline script), plus mutual exclusion with panels.

   All static node lists are cached in the constructor; every listener
   is registered against one AbortController so a theme-editor section
   re-render (disconnect) detaches them and can't leak the subtree.
   ============================================================ */

/* The only things a pointerdown may land on without dismissing the open
   search. Not "the search bar" — the field itself, plus the two cases
   that would break if the box shut underneath them: the toggle owns its
   own open/close, and a result must survive being pressed, because
   closing collapses the box to max-width:0 and would take the link out
   from under the finger before its click ever landed. */
const KEEP_SEARCH_OPEN = [
  '#searchToggle',
  '#searchBar .search__input',
  '#searchBar .search__button',
  '#searchBar .predictive-search a',
  '#searchBar .predictive-search button',
].join(',')

if (!customElements.get('desktop-nav')) {
  customElements.define(
    'desktop-nav',
    class DesktopNav extends HTMLElement {
      constructor() {
        super()
        this.openDoor = null
        this.openTimer = null
        this.closeTimer = null
        this.panelHideTimer = null
        /* Input-type truth comes from Pointer Events, never from hover/
           pointer media queries: iPadOS answers those for the touchscreen
           even when a trackpad is attached (and can lie the other way in
           desktop-class mode). */
        this.lastPointerType = ''

        this.doors = Array.from(this.querySelectorAll('.co-dnav__door[data-door]'))
        this.panels = new Map()
        this.doors.forEach((door) => {
          const panel = this.querySelector(`[data-panel="${door.dataset.door}"]`)
          if (panel) this.panels.set(door.dataset.door, panel)
        })
        this.paneTriggers = Array.from(this.querySelectorAll('[data-pane-trigger]'))
        this.panes = Array.from(this.querySelectorAll('.co-dnav__pane'))
        /* [data-card], not .co-dnav__card: a standalone rail item's
           product row carries the same attribute, so one swap governs
           both and a row can never show alongside a department card. */
        this.cards = Array.from(this.querySelectorAll('[data-card]'))

        this.scrim = document.querySelector('.header__bg')
        this.searchToggle = this.querySelector('#searchToggle')
        this.searchBar = this.querySelector('#searchBar')
        this.searchInput = this.querySelector('#Search-In-Modal')
        this.searchPredictive = this.querySelector('#searchBar predictive-search')
        this.brandFind = this.querySelector('[data-brand-find]')
        this.brandFindTrigger = this.querySelector('[data-brand-find-trigger]')
        this.brandFilter = this.querySelector('[data-brand-filter]')
        this.brandEmpty = this.querySelector('[data-brand-empty]')
        /* The A–Z is static after render — index it once so each filter
           pass reads the array, not hundreds of DOM attributes. */
        this.brandIndex = Array.from(this.querySelectorAll('.co-dnav__brand-group')).map(
          (group) => ({
            group,
            rows: Array.from(group.querySelectorAll('.co-dnav__brand')).map((el) => ({
              el,
              name: el.dataset.brand || '',
            })),
          })
        )
      }

      connectedCallback() {
        this.listeners = new AbortController()
        const signal = { signal: this.listeners.signal }

        this.addEventListener(
          'pointerdown',
          (e) => {
            this.lastPointerType = e.pointerType
          },
          { capture: true, signal: this.listeners.signal }
        )

        this.doors.forEach((door) => {
          door.addEventListener(
            'click',
            () => {
              this.cancelTimers()
              if (this.openDoor === door.dataset.door) {
                /* A click right after hover-intent already opened this
                   panel is the user "committing", not dismissing — don't
                   snap it shut under them. */
                if (performance.now() - (this.openedAt || 0) > 500) this.close(false)
              } else {
                this.open(door.dataset.door)
              }
            },
            signal
          )
          door.addEventListener(
            'mouseenter',
            () => {
              this.cancelTimers()
              if (this.openDoor === door.dataset.door) return
              /* Touch: WebKit fires this synthetically on tap. Open NOW —
                 a synchronous mutation makes WebKit treat the tap as
                 hover and swallow the click (the old menu's behaviour);
                 if the click still arrives, the grace window in the
                 click handler keeps it from closing the panel again. */
              if (this.isTouch()) {
                this.open(door.dataset.door)
                return
              }
              /* A non-touch mouseenter IS a hovering pointer — no media
                 query gate. iPadOS reports the touchscreen (hover:none)
                 even with a trackpad attached, so gating on the query
                 killed hover for iPad trackpad users. The old menu had
                 no gate; neither do we. */
              this.openTimer = setTimeout(() => this.open(door.dataset.door), 90)
            },
            signal
          )
          door.addEventListener('mouseleave', () => clearTimeout(this.openTimer), signal)
        })

        /* Close when the pointer leaves the door row AND its panel. Mouse
           only: a finger never "leaves", but iPadOS still emits synthetic
           boundary events around a tap, and arming a 120ms close off one
           of those shuts the panel mid-gesture.

           Focus inside the header also blocks it, because then the user
           is working the panel by KEYBOARD and where the pointer sits is
           not a statement about whether they are done. iPadOS hides the
           trackpad cursor the instant you start typing, which emits a
           mouseleave — so searching Brands from an iPad armed this timer
           on the first keystroke and shut the menu 120ms later, just late
           enough to see the letter land. A trackpad reports pointerType
           'mouse', so the touch exemption above never covered it. */
        this.addEventListener(
          'mouseleave',
          () => {
            if (!this.openDoor || this.isTouch()) return
            if (this.contains(document.activeElement)) return
            this.closeTimer = setTimeout(() => this.close(false), 120)
          },
          signal
        )
        this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer), signal)
        /* Focus arriving cancels a pending hover-leave close for the same
           reason mouseenter does — the user is engaging, not leaving.
           This closes the race where the cursor-hide mouseleave arms the
           timer a tick before the keystroke moves focus into the filter,
           which the guard above can only catch once focus has landed. */
        this.addEventListener('focusin', () => clearTimeout(this.closeTimer), signal)

        /* Outside-press dismissal for panels, the same mechanism the
           search uses. It replaces a click handler on the scrim, which
           looked like the dismiss target but could not be trusted as one:
           .header__bg is trapped at effective z-index 3 by its sticky
           parent section, so anything stacked above that swallows the
           press. This asks a question about the DOM instead of about
           paint order, and it is the only dismissal touch has now that
           hover-leave is correctly mouse-only. The doors and every panel
           live inside <desktop-nav>, so containment is the whole test. */
        document.addEventListener(
          'pointerdown',
          (e) => {
            if (!this.openDoor) return
            if (this.contains(e.target)) return
            this.close(false)
          },
          signal
        )

        /* Click-away for the search, on the document rather than on the
           scrim. The scrim looks like the dismiss target but cannot be
           trusted as one: it carries z-index:auto and sits early in the
           DOM, so any positioned element later in the page hit-tests
           above it and swallows the click.

           The rule is simply "anything but the field dismisses". Scoping
           it to "outside #searchBar" is not the same thing and left dead
           zones the user kept landing in — above all the dropdown itself,
           a 380x675 panel sitting exactly where you reach to dismiss it. */
        document.addEventListener(
          'pointerdown',
          (e) => {
            if (!this.searchIsOpen()) return
            if (e.target.closest?.(KEEP_SEARCH_OPEN)) return
            /* A door with a panel hands off to open(), which closes the
               search itself; closing here too would drop the scrim on
               pointerdown and raise it again on click — one gesture, two
               states, a visible dip. Doors without a panel are plain
               links with no such handoff, so they must dismiss here or
               nothing does. */
            const door = e.target.closest?.('.co-dnav__door[data-door]')
            if (door && this.panels.has(door.dataset.door)) return
            this.setSearchOpen(false)
          },
          signal
        )

        /* Opening the cart drawer dismisses any open panel. */
        const cartToggle = this.querySelector('[data-ajax-cart-toggle-class-button]')
        if (cartToggle) cartToggle.addEventListener('click', () => this.close(false), signal)

        document.addEventListener('keydown', (e) => this.onDocumentKeydown(e), signal)

        /* Close when focus MOVES to something outside the header — the
           keyboard user tabbing out of the menu, which is the only thing
           this path is for. Pressing outside is dismissed by the
           pointerdown handler above, which is input-agnostic.

           A focusout carrying no relatedTarget never closes, whatever
           the input. iPadOS emits exactly that — no relatedTarget, and
           document.hasFocus() still true — when it engages the keyboard
           accessory bar as you begin typing, which shut the menu under
           anyone searching Brands. That was first patched by exempting
           touch, but an iPad with a trackpad reports pointerType 'mouse',
           so the exemption never applied and the bug survived for
           trackpad users. Input type was never the right question: a null
           relatedTarget carries no information about where focus went, so
           it cannot justify closing anything. Same lesson as PRs #73/#74
           — do not branch this behaviour on how the user is pointing. */
        this.addEventListener(
          'focusout',
          (e) => {
            if (!this.openDoor) return
            if (!e.relatedTarget) return
            if (this.contains(e.relatedTarget)) return
            this.close(false)
          },
          signal
        )

        /* Rail: hover (with a small intent delay) or focus selects the
           pane; click falls through and navigates. On touch (no hover
           exists) the first tap previews the pane instead, and tapping
           the already-selected department navigates — otherwise panes
           are unreachable on iPads wide enough for the desktop nav. */
        this.paneTriggers.forEach((item) => {
          let railTimer
          item.addEventListener(
            'mouseenter',
            () => {
              /* Touch: synthetic on tap — swap synchronously (WebKit then
                 swallows the click, like the old menu). The swap is
                 recorded so the click handler can cancel navigation in
                 engines that send the click anyway. */
              if (this.isTouch()) {
                this.selectPane(item.dataset.paneTrigger)
                return
              }
              railTimer = setTimeout(() => this.selectPane(item.dataset.paneTrigger), 60)
            },
            signal
          )
          item.addEventListener('mouseleave', () => clearTimeout(railTimer), signal)
          item.addEventListener('focus', () => this.selectPane(item.dataset.paneTrigger), signal)
          item.addEventListener(
            'click',
            (e) => {
              if (!this.isTouch()) return
              /* Navigate only when this department was already selected
                 BEFORE this tap — a selection made milliseconds ago by
                 this same tap's synthetic hover doesn't count. */
              const justSwapped =
                this.lastPaneSelect &&
                this.lastPaneSelect.key === item.dataset.paneTrigger &&
                performance.now() - this.lastPaneSelect.t < 500
              if (!item.classList.contains('is-selected') || justSwapped) {
                e.preventDefault()
                this.selectPane(item.dataset.paneTrigger)
              }
            },
            signal
          )
        })

        /* Brands filter — the panel opens with the title in place;
           the pointer moving INTO the section performs the transform
           (box slides open, unfocused) and it stays until the menu
           closes (reset in close()). Clicking in or typing engages
           it; the title button stays the click/Enter path for touch
           and keyboard. */
        if (this.brandFilter) {
          if (this.brandFindTrigger) {
            this.brandFindTrigger.addEventListener('click', () => this.openBrandFind(), signal)
          }
          const brandsPanel = this.brandFilter.closest('.co-dnav__panel')
          if (brandsPanel) {
            brandsPanel.addEventListener(
              'mouseenter',
              () => this.openBrandFind(false),
              signal
            )
          }
          this.brandFilter.addEventListener(
            'input',
            debounce(() => this.applyBrandFilter(), 220),
            signal
          )
        }

        /* Search toggle — moved from the section's inline script.
           Same behaviour, plus panel/search mutual exclusion. */
        if (this.searchToggle && this.searchBar) {
          this.searchToggle.addEventListener(
            'click',
            () => {
              const opening = !this.searchIsOpen()
              if (opening) this.close(false)
              this.setSearchOpen(opening)
            },
            signal
          )
        }
      }

      disconnectedCallback() {
        if (this.listeners) this.listeners.abort()
        this.cancelTimers()
        clearTimeout(this.panelHideTimer)
        /* A theme-editor re-render can detach this instance with a panel
           open; the fresh one renders closed, so the lock must not stay
           raised on its behalf. */
        document.body.classList.remove('js-dnav-open')
      }

      cancelTimers() {
        clearTimeout(this.openTimer)
        clearTimeout(this.closeTimer)
      }

      /* True when the current interaction came from a finger. The last
         physical pointerdown is the authority: synthetic compatibility
         clicks can carry a misleading pointerType, and the hover media
         query outright lies on iPadOS. */
      isTouch() {
        return this.lastPointerType === 'touch'
      }

      /* The one place the search open/closed state is written. */
      setSearchOpen(open) {
        if (!this.searchBar) return
        /* Home page with the chain still riding in the flow under the landing
           banner (html.co-chain-flow, sections/announcement-bar.liquid): the
           header is part-way down the screen, and the dropdown, sized to the
           viewport below a PINNED header (component-predictive-search.css),
           would run under the fold with the page about to lock. Bring the
           page to the chain's pin first — the top of <main>, where the chain
           becomes sticky (#209) — so the header sits at the top before the
           box opens (Tom, 2026-09-11: "this should pull the header to the
           top so you can see the full search"). Instant, not smooth: it has
           landed before syncOverlay() raises the lock, so the lock anchors
           to the new position, and the page's scroll-behavior: smooth would
           otherwise make this an animation for the lock to fight. An element
           position, never a chrome measurement; a page already past the pin
           is left where it is. */
        if (open && document.documentElement.classList.contains('co-chain-flow')) {
          const main = document.getElementById('MainContent')
          if (main) {
            const pin = main.getBoundingClientRect().top + window.scrollY
            if (window.scrollY < pin) window.scrollTo({ top: pin, behavior: 'instant' })
          }
        }
        this.searchBar.classList.toggle('active', open)
        this.searchToggle.classList.toggle('active', !open)
        this.searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false')

        if (open) {
          if (this.searchInput) this.searchInput.focus()
        } else {
          /* Hand the field back at rest, not merely collapsed. The box
             animates shut on its own, but predictive-search keeps its own
             open attribute and the typed term: without this the dropdown
             stays open inside the collapsing box, and the next expand
             reveals the last visit's results already showing. close(true)
             is its own clear-the-term path. */
          this.searchPredictive?.close(true)
          if (this.searchInput && document.activeElement === this.searchInput) {
            this.searchInput.blur()
          }
        }

        this.syncOverlay()
      }

      searchIsOpen() {
        return !!this.searchBar && this.searchBar.classList.contains('active')
      }

      /* The scrim and the page lock are shared by the mega menu and the
         search dropdown, so neither may write them directly — one owner,
         derived from both states, or whichever closes last wins and
         strands the dim (or leaves the page frozen). body.js-dnav-open is
         a trigger class of the shared overlay lock (global.js): while it
         is on, the page behind the scrim cannot scroll, exactly as behind
         the cart and filter drawers. */
      syncOverlay() {
        const open = !!this.openDoor || this.searchIsOpen()
        if (this.scrim) this.scrim.classList.toggle('active', open)
        document.body.classList.toggle('js-dnav-open', open)
      }

      open(key) {
        const panel = this.panels.get(key)
        if (!panel || this.openDoor === key) return
        if (this.openDoor) this.hidePanel(this.openDoor)
        this.openDoor = key
        this.openedAt = performance.now()

        /* Opening a panel dismisses the inline search. */
        if (this.searchIsOpen()) this.setSearchOpen(false)

        clearTimeout(this.panelHideTimer)
        panel.hidden = false
        /* Force a style flush so the open transition runs from the hidden
           state (a rAF here can starve in background tabs). */
        void panel.offsetWidth
        panel.classList.add('is-open')
        this.doorFor(key)?.setAttribute('aria-expanded', 'true')
        this.syncOverlay()
        this.selectFirstPane(panel) /* no-ops on panels without a rail */
      }

      /* refocus=true only for keyboard closes (Escape): a scripted
         door.focus() draws the :focus-visible ring, which pointer
         users should never see. */
      close(refocus = true) {
        if (!this.openDoor) return
        const key = this.openDoor
        this.openDoor = null
        this.hidePanel(key)
        this.syncOverlay()
        if (refocus) this.doorFor(key)?.focus()
        this.resetBrandFilter()
      }

      hidePanel(key) {
        const panel = this.panels.get(key)
        const door = this.doorFor(key)
        if (door) door.setAttribute('aria-expanded', 'false')
        if (!panel) return
        panel.classList.remove('is-open')
        /* display:none once the close transition actually ends (however
           long the CSS makes it), with a timeout as the real fallback
           for a missed event. Reopening clears both paths. */
        const finish = (e) => {
          if (e && e.target !== panel) return
          panel.removeEventListener('transitionend', finish)
          clearTimeout(this.panelHideTimer)
          if (!panel.classList.contains('is-open')) panel.hidden = true
        }
        panel.addEventListener('transitionend', finish)
        clearTimeout(this.panelHideTimer)
        this.panelHideTimer = setTimeout(finish, 600)
      }

      doorFor(key) {
        return this.doors.find((d) => d.dataset.door === key) || null
      }

      /* ---- Shop rail ---------------------------------------- */
      selectFirstPane(panel) {
        const first = panel.querySelector('[data-pane-trigger]')
        if (first) this.selectPane(first.dataset.paneTrigger)
      }

      /* The hidden attribute is the single source of truth for which
         pane/card shows; the CSS animates on :not([hidden]). */
      selectPane(key) {
        if (!key) return
        if (!this.lastPaneSelect || this.lastPaneSelect.key !== key) {
          this.lastPaneSelect = { key, t: performance.now() }
        }
        this.paneTriggers.forEach((item) => {
          item.classList.toggle('is-selected', item.dataset.paneTrigger === key)
        })
        this.panes.forEach((pane) => (pane.hidden = pane.dataset.pane !== key))
        this.cards.forEach((card) => (card.hidden = card.dataset.card !== key))
      }

      /* ---- Brands ------------------------------------------- */
      applyBrandFilter() {
        if (!this.brandFilter) return
        const q = (this.brandFilter.value || '').trim().toLowerCase()
        let total = 0
        this.brandIndex.forEach(({ group, rows }) => {
          let visible = 0
          rows.forEach(({ el, name }) => {
            const match = !q || name.indexOf(q) !== -1
            el.hidden = !match
            if (match) visible++
          })
          group.hidden = visible === 0
          total += visible
        })
        if (this.brandEmpty) this.brandEmpty.hidden = total !== 0
      }

      openBrandFind(focus = true) {
        if (!this.brandFind) return
        this.brandFind.classList.add('is-open')
        if (this.brandFindTrigger) this.brandFindTrigger.classList.add('is-hidden')
        /* Hover opens without stealing focus — the click (or typing)
           is what puts the cursor in. */
        if (focus) this.brandFilter.focus()
      }

      closeBrandFind() {
        if (!this.brandFind) return
        this.brandFind.classList.remove('is-open')
        if (this.brandFindTrigger) this.brandFindTrigger.classList.remove('is-hidden')
      }

      resetBrandFilter() {
        this.closeBrandFind()
        if (!this.brandFilter || !this.brandFilter.value) return
        this.brandFilter.value = ''
        this.applyBrandFilter()
      }

      /* ---- Keyboard ------------------------------------------ */
      onDocumentKeydown(e) {
        if (e.key === 'Escape' && this.openDoor) {
          this.close()
          return
        }

        /* Escape closes the search too, now that it dims the page — a lit
           scrim with no obvious way out is worse than no scrim at all. */
        if (e.key === 'Escape' && this.searchIsOpen()) {
          this.setSearchOpen(false)
          this.searchToggle?.focus()
          return
        }

        /* Type-anywhere: with the Brands panel open, printable keys and
           Backspace route to the filter. Modifier combos, IME
           composition and typing in any other field pass through. */
        if (this.openDoor !== 'brands' || !this.brandFilter) return
        if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return
        const active = document.activeElement
        if (active === this.brandFilter) return
        if (
          active &&
          (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
        )
          return
        const printable = e.key.length === 1
        if (!printable && e.key !== 'Backspace') return
        e.preventDefault()
        if (printable) {
          this.brandFilter.value += e.key
        } else {
          this.brandFilter.value = this.brandFilter.value.slice(0, -1)
        }
        this.brandFilter.dispatchEvent(new Event('input', { bubbles: true }))
        this.openBrandFind()
      }
    }
  )
}
